import {useEffect, useMemo} from 'react'
import {useSafeChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {getAgentSessionsTokenProvider} from '@github-ui/copilot-auth-token/agent-sessions-token'

const PROTECTED_ORGS_CACHE_KEY = 'PROTECTED_ORGS'

/**
 * Custom hook to detect SSO changes and clear relevant cache.
 *
 * When a user logs in or out of SSO orgs we should:
 * - Clear auth token from localStorage
 * - Clear cached tasks from localStorage
 * - Invalidate React Query cache to force a refetch
 *
 * Accepts optional SSO org IDs directly so it can be used outside of the CopilotChatProvider.
 * When no argument is provided, falls back to reading from CopilotChatProvider context.
 */
export function useSSOChangeDetection(ssoOrgIDsInput?: string[]) {
  const chatState = useSafeChatState()
  const ssoOrganizations = chatState?.ssoOrganizations

  // Sort the orgs to prevent unnecessary effect re-runs
  const ssoOrgIDs = useMemo(() => {
    if (ssoOrgIDsInput) {
      return [...ssoOrgIDsInput].sort()
    }
    return (ssoOrganizations ?? []).map(org => org.id).sort()
  }, [ssoOrgIDsInput, ssoOrganizations])

  useEffect(() => {
    const tokenProvider = getAgentSessionsTokenProvider()
    tokenProvider.ssoOrgIDs = ssoOrgIDs

    const currentOrgsString = JSON.stringify(ssoOrgIDs)
    const prevOrgsString = sessionStorage.getItem(PROTECTED_ORGS_CACHE_KEY)

    // Setting the orgs on initial load or initial hydration
    if (prevOrgsString === null || (prevOrgsString === '[]' && ssoOrgIDs.length > 0)) {
      sessionStorage.setItem(PROTECTED_ORGS_CACHE_KEY, currentOrgsString)
      return
    }

    if (currentOrgsString === prevOrgsString) {
      return
    }

    tokenProvider.removeLocalStorageAuthToken()
    sessionStorage.setItem(PROTECTED_ORGS_CACHE_KEY, currentOrgsString)
  }, [ssoOrgIDs])
}
